import React, { useCallback } from 'react';
import FieldShell from './FieldShell';
import { useDraftField } from '../hooks/useDraftField';
import { useFieldLifecycle } from '../hooks/useFieldLifecycle';

export default function DebouncedTextField({
  as = 'input',
  value,
  onCommit,
  delay = 500,
  label,
  helpText,
  error,
  inputId,
  width,
  tapTarget,
  startAdornment,
  endAdornment,
  onBlur,
  onKeyDown,
  onChange,
  cancelOnEscape = false,
  submitOnEnter = false,
  blurAfterSubmit = false,
  blurAfterCancel = false,
  flushOnBlur = true,
  unmountPolicy = 'cancel',
  ...rest
}) {
  const { draft, cancel, flush, scheduleCommit } = useDraftField({ value, onCommit, unmountPolicy });
  const { handleBlur, handleKeyDown } = useFieldLifecycle({
    flush,
    cancel,
    onBlur,
    onKeyDown,
    flushOnBlur,
    cancelOnEscape,
    submitOnEnter,
    blurAfterSubmit,
    blurAfterCancel,
  });

  const handleChange = useCallback((event) => {
    scheduleCommit(event.target.value, delay);
    if (onChange) onChange(event);
  }, [delay, onChange, scheduleCommit]);

  const Tag = as;
  const control = (
    <Tag
      {...rest}
      value={draft}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );

  const shouldWrapWithShell = !!(label || helpText || error || inputId || width || tapTarget || startAdornment || endAdornment);
  if (!shouldWrapWithShell) {
    return control;
  }

  return (
    <FieldShell
      label={label}
      helpText={helpText}
      error={error}
      inputId={inputId}
      width={width}
      tapTarget={tapTarget}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
    >
      {control}
    </FieldShell>
  );
}
